import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { PrimaryBtn, GhostBtn, Field, Input, Select } from "../components/kit";

const LEVELS = ["Recreational", "Local junior tournaments", "Regional tournaments", "Provincial/state", "National"];
const FACILITIES = ["Driving range", "Course access", "Putting green", "Home practice", "Gym"];
const EQUIP = ["No equipment", "Bands", "Dumbbells", "Medicine ball", "Full gym"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DIAG = {
  driving: ["Slice", "Hook", "Push", "Pull", "Poor contact", "Low distance", "Penalties", "Inconsistent direction"],
  irons: ["Fat", "Thin", "Toe", "Heel", "Pull", "Push", "Poor distance control", "Poor contact"],
  wedges: ["Distance control", "Contact", "Partial wedges", "Direction"],
  chipping: ["Thin", "Fat", "Poor landing spot", "Poor distance control", "Chips finishing too far"],
  putting: ["3-putts", "Short putts", "Start line", "Speed control", "Lag putting", "Breaking putts"],
};

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
      active ? "bg-[#CCFF00] text-[#0A0A0A] border-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00]"
    }`}>{children}</button>
);

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const in9 = new Date(Date.now() + 9 * 86400000);

  const [p, setP] = useState({
    player_name: "", age: 14, gender: "", handicap: 20, avg_score: "", lowest_score: "",
    dominant_hand: "Right", competitive_level: "Local junior tournaments",
    facilities: ["Driving range", "Putting green"], equipment: "Full set",
    strengths: [], strength_equipment: "No equipment", minutes_per_day: 90,
    practice_days: [...DAYS], physical_limitations: "",
    diagnostics: { driving: [], irons: [], wedges: [], chipping: [], putting: [] },
  });
  const [t, setT] = useState({ name: "", tournament_date: iso(in9), start_date: iso(today), course_par: 72, course_yardage: 6200 });
  const [rounds, setRounds] = useState([{ score: "", putts: "", three_putts: "", penalties: "", gir: "", fairways_hit: "" }]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setP((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));
  const toggleDiag = (cat, v) => setP((s) => ({ ...s, diagnostics: { ...s.diagnostics, [cat]: s.diagnostics[cat].includes(v) ? s.diagnostics[cat].filter((x) => x !== v) : [...s.diagnostics[cat], v] } }));

  const steps = ["Golfer", "Diagnostic", "Setup", "Tournament", "Rounds"];

  const finish = async () => {
    if (!p.player_name) { toast.error("Enter player name"); setStep(0); return; }
    if (!t.name) { toast.error("Enter tournament name"); setStep(3); return; }
    setBusy(true);
    try {
      await api.post("/profile", {
        ...p, age: Number(p.age), handicap: Number(p.handicap),
        avg_score: p.avg_score ? Number(p.avg_score) : null,
        lowest_score: p.lowest_score ? Number(p.lowest_score) : null,
        minutes_per_day: Number(p.minutes_per_day),
      });
      await api.post("/tournament", { ...t, course_par: Number(t.course_par), course_yardage: Number(t.course_yardage) });
      for (const r of rounds) {
        if (r.score) {
          await api.post("/rounds", {
            score: Number(r.score), course_par: Number(t.course_par) || 72,
            putts: r.putts ? Number(r.putts) : null, three_putts: r.three_putts ? Number(r.three_putts) : null,
            penalties: r.penalties ? Number(r.penalties) : null, gir: r.gir ? Number(r.gir) : null,
            fairways_hit: r.fairways_hit ? Number(r.fairways_hit) : null,
          });
        }
      }
      toast.loading("Analyzing performance & building your plan…", { id: "gen" });
      await api.post("/plan/generate");
      toast.success("Your plan is ready!", { id: "gen" });
      navigate("/");
    } catch (err) {
      toast.error("Could not build plan. Try again.", { id: "gen" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#CCFF00] mb-2">Onboarding · Step {step + 1} of 5</div>
        <h1 className="font-heading text-3xl uppercase mb-6">{steps[step]}</h1>
        <div className="flex gap-1 mb-8">
          {steps.map((_, i) => <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-[#CCFF00]" : "bg-[#262626]"}`} />)}
        </div>

        <div className="border border-[#262626] bg-[#141414] p-6 space-y-5">
          {step === 0 && (
            <>
              <Field label="Player name"><Input data-testid="ob-name" value={p.player_name} onChange={(e) => set("player_name", e.target.value)} placeholder="e.g. Jordan Miller" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Age"><Input data-testid="ob-age" type="number" value={p.age} onChange={(e) => set("age", e.target.value)} /></Field>
                <Field label="Gender (optional)"><Select value={p.gender} onChange={(e) => set("gender", e.target.value)}><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></Select></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Handicap"><Input data-testid="ob-handicap" type="number" step="0.1" value={p.handicap} onChange={(e) => set("handicap", e.target.value)} /></Field>
                <Field label="Avg 18 score"><Input type="number" value={p.avg_score} onChange={(e) => set("avg_score", e.target.value)} /></Field>
                <Field label="Lowest score"><Input type="number" value={p.lowest_score} onChange={(e) => set("lowest_score", e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Dominant hand"><Select value={p.dominant_hand} onChange={(e) => set("dominant_hand", e.target.value)}><option>Right</option><option>Left</option></Select></Field>
                <Field label="Competitive level"><Select value={p.competitive_level} onChange={(e) => set("competitive_level", e.target.value)}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</Select></Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-sm text-[#A3A3A3]">Where are strokes being lost? Select all that apply.</p>
              {Object.entries(DIAG).map(([cat, opts]) => (
                <div key={cat}>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2">{cat}</div>
                  <div className="flex flex-wrap gap-2">
                    {opts.map((o) => <Chip key={o} active={p.diagnostics[cat].includes(o)} onClick={() => toggleDiag(cat, o)}>{o}</Chip>)}
                  </div>
                </div>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2">Facility access</div>
                <div className="flex flex-wrap gap-2">{FACILITIES.map((f) => <Chip key={f} active={p.facilities.includes(f)} onClick={() => toggleArr("facilities", f)}>{f}</Chip>)}</div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2">Practice days</div>
                <div className="flex flex-wrap gap-2">{DAYS.map((d) => <Chip key={d} active={p.practice_days.includes(d)} onClick={() => toggleArr("practice_days", d)}>{d.slice(0, 3)}</Chip>)}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Minutes per day"><Input data-testid="ob-minutes" type="number" value={p.minutes_per_day} onChange={(e) => set("minutes_per_day", e.target.value)} /></Field>
                <Field label="Strength equipment"><Select value={p.strength_equipment} onChange={(e) => set("strength_equipment", e.target.value)}>{EQUIP.map((x) => <option key={x}>{x}</option>)}</Select></Field>
              </div>
              <Field label="Physical limitations / injuries (optional)" hint="If pain is present, we recommend rest & a qualified professional — we won't prescribe exercises to treat it.">
                <Input value={p.physical_limitations} onChange={(e) => set("physical_limitations", e.target.value)} placeholder="e.g. tight lower back" />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="Tournament name"><Input data-testid="ob-tname" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} placeholder="e.g. Regional Junior Open" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Training start date"><Input data-testid="ob-start" type="date" value={t.start_date} onChange={(e) => setT({ ...t, start_date: e.target.value })} /></Field>
                <Field label="Tournament date"><Input data-testid="ob-tdate" type="date" value={t.tournament_date} onChange={(e) => setT({ ...t, tournament_date: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Course par"><Input type="number" value={t.course_par} onChange={(e) => setT({ ...t, course_par: e.target.value })} /></Field>
                <Field label="Course yardage"><Input type="number" value={t.course_yardage} onChange={(e) => setT({ ...t, course_yardage: e.target.value })} /></Field>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="text-sm text-[#A3A3A3]">Add recent rounds (optional but improves accuracy). At least one recommended.</p>
              {rounds.map((r, i) => (
                <div key={i} className="grid grid-cols-3 gap-3 border border-[#262626] p-3">
                  <Field label="Score"><Input type="number" value={r.score} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, score: e.target.value } : x))} /></Field>
                  <Field label="Putts"><Input type="number" value={r.putts} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, putts: e.target.value } : x))} /></Field>
                  <Field label="3-putts"><Input type="number" value={r.three_putts} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, three_putts: e.target.value } : x))} /></Field>
                  <Field label="Penalties"><Input type="number" value={r.penalties} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, penalties: e.target.value } : x))} /></Field>
                  <Field label="GIR"><Input type="number" value={r.gir} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, gir: e.target.value } : x))} /></Field>
                  <Field label="Fairways"><Input type="number" value={r.fairways_hit} onChange={(e) => setRounds(rounds.map((x, j) => j === i ? { ...x, fairways_hit: e.target.value } : x))} /></Field>
                </div>
              ))}
              {rounds.length < 5 && <GhostBtn type="button" onClick={() => setRounds([...rounds, { score: "", putts: "", three_putts: "", penalties: "", gir: "", fairways_hit: "" }])}>+ Add round</GhostBtn>}
            </>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <GhostBtn onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>Back</GhostBtn>
          {step < 4
            ? <PrimaryBtn data-testid="ob-next" onClick={() => setStep(step + 1)}>Continue</PrimaryBtn>
            : <PrimaryBtn data-testid="ob-finish" onClick={finish} disabled={busy}>{busy ? "Building…" : "Build My Plan"}</PrimaryBtn>}
        </div>
      </div>
    </div>
  );
}
